import {
    type HierarcicalObjectReference,
    type ObjectData,
    type ObjectDB,
    type ObjectId,
    type SearchOptions,
} from "@novorender/data-js-api";

import { getProcoreObjectMetadata } from "./bimObjectMetadata";
import { procoreResolveInstanceIdsToObjectIds, procoreResolveObjectIdsToInstanceIds } from "./bimObjectResolve";
import { searchProcoreObjects } from "./bimObjectSearch";
import { streamProcoreSceneMetadata, streamProcoreSceneMetadataBatches } from "./bimSceneMetadata";
import { type BimSignedFileUrlParams, createBimSignedFileUrl } from "./bimSignedDownloadUrl";

/**
 * ObjectDB implementation for Procore BIM Search DB API.
 *
 * Provides object metadata, search, and hierarchy queries by calling
 * the Procore BIM Search DB endpoints.
 */
export class ProcoreObjectDB implements ObjectDB {
    /**
     * @param sceneSliceId The slice of `sceneVersionId` this session was opened on, when
     *   it was opened on one. It reaches the FAS reads below — the `descendants` files and
     *   the metadata dump live under the slice rather than the version. The search-DB
     *   endpoints still answer version-level (see BM-13591).
     */
    constructor(
        private readonly companyId: number,
        private readonly projectId: number,
        private readonly sceneId: string,
        private readonly sceneVersionId: string,
        private readonly sceneSliceId?: string,
    ) {}

    /** What the FAS reads address: this session's slice when it has one, the version otherwise. */
    private get fileUrlParams(): BimSignedFileUrlParams {
        return {
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            sceneSliceId: this.sceneSliceId,
        };
    }

    async loadCompleteMetadata(id: number): Promise<ObjectData> {
        return this.getObjectMetdata(id);
    }

    async getObjectMetdata(id: number): Promise<ObjectData> {
        return getProcoreObjectMetadata({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            objectId: id,
        });
    }

    async *search(
        filter: SearchOptions,
        signal: AbortSignal | undefined,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        yield* searchProcoreObjects({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            filter,
            signal,
        });
    }

    /**
     * Stream every node in the scene by downloading the FAS `metadata` dump once
     * (see {@link streamProcoreSceneMetadata}). Not part of the `ObjectDB`
     * interface — a bulk convenience for callers that need the whole scene at
     * once (e.g. the NodeClass debug panel) instead of paginating `search`.
     */
    async *streamSceneMetadata(signal?: AbortSignal): AsyncIterableIterator<ObjectData> {
        yield* streamProcoreSceneMetadata(this.fileUrlParams, signal);
    }

    async *streamSceneMetadataBatches(signal?: AbortSignal): AsyncIterableIterator<ObjectData[]> {
        yield* streamProcoreSceneMetadataBatches(this.fileUrlParams, signal);
    }

    /**
     * @param fastPathOnly When `true`, resolve only via the pre-computed
     *   `descendants` file and rethrow if it is missing, instead of falling
     *   back to the (potentially unbounded) subtree search. Background cache
     *   warm-ups pass this so a scene whose descendants files are absent does
     *   not trigger thousands of paginated search requests; the expensive
     *   fallback then runs only for a deliberate, user-initiated selection.
     */
    async descendants(
        object: HierarcicalObjectReference,
        signal: AbortSignal | undefined,
        fastPathOnly = false,
        skipFastPath = false,
    ): Promise<ObjectId[]> {
        // `skipFastPath` is set by callers that already know the pre-computed file
        // is missing (see the negative cache in utils/search). Skipping the doomed
        // fetch avoids a guaranteed 404 and goes straight to the search fallback.
        if (!skipFastPath) {
            try {
                // Fast path: Try to fetch pre-computed descendants file.
                // `createBimSignedFileUrl` handles signed-URL caching, dev-proxy
                // rewriting, and `?file=` parameter assembly in one call.
                const descendantsUrl = await createBimSignedFileUrl(this.fileUrlParams, `descendants/${object.id}`);

                const response = await fetch(descendantsUrl, { signal });

                if (!response.ok) {
                    throw new Error(`Failed to fetch descendants file: ${response.status}`);
                }

                const text = await response.text();

                // Parse newline-delimited IDs
                return text
                    .trim()
                    .split("\n")
                    .map((line) => parseInt(line.trim(), 10))
                    .filter((id) => !isNaN(id));
            } catch (error) {
                // A caller that only wants the cheap pre-computed file (background
                // preload) must not pay for the unbounded fallback scan.
                if (fastPathOnly) {
                    throw error;
                }
                // Otherwise fall through to the search fallback below.
            }
        }

        // Fallback: Use search endpoint if descendants file doesn't exist, was
        // skipped, or the fetch failed. This handles cases where the file might
        // not be available for all objects.
        const ids: ObjectId[] = [];

        for await (const result of searchProcoreObjects({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            filter: {
                parentPath: object.path,
                descentDepth: undefined, // undefined = all descendants
            },
            signal,
        })) {
            ids.push(result.id);
        }

        return ids;
    }

    async resolveObjectIdsToInstanceIds(
        objectIds: ObjectId[],
        signal: AbortSignal | undefined,
    ): Promise<(string | null)[]> {
        return procoreResolveObjectIdsToInstanceIds({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            objectIds,
            signal,
        });
    }

    async resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal: AbortSignal | undefined,
    ): Promise<(ObjectId | null)[]> {
        return procoreResolveInstanceIdsToObjectIds({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            instanceIds,
            signal,
        });
    }
}
